import React from 'react';
import ENV from '../../../../commons/environment';
import { Button, Grid, LinearProgress, MenuItem, TextField } from '@mui/material';
import { User } from '../../../../models/interfaces/User.interface';
import { BusinessObject } from '../../../../models/interfaces/BusinessObject.interface';
import storageService from '../../../../services/storage.service';
import logger from '../../../../commons/logger';
import { PersonAdd } from '@mui/icons-material';
import userService from '../../../../services/user.service';
import businessObjectService from '../../../../services/business-object.service';
import authenticationService from '../../../../services/auth.service';
import CreateNewUserTokenDialogComponent from './components/create-new-user-token-dialog.component';

export interface TokenInfo {
  user: User;
  boList: BusinessObject[];
  token: string;
}

const WATCHLIST_STORAGE_KEY = 'WATCHLIST';

export default function UserWatchlistComponent() {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isOpenDialog, setIsOpenDialog] = React.useState<boolean>(false);
  const [watchList, setWatchList] = React.useState<TokenInfo[]>([]);
  const [selectedTokenInfo, setSelectedTokenInfo] = React.useState<TokenInfo>(null);

  const [beautyAdminUrl, setBeautyAdminUrl] = React.useState<string>('');
  const [beautyCheckinUrl, setBeautyCheckinUrl] = React.useState<string>('');
  const [beautyCheckoutUrl, setBeautyCheckoutUrl] = React.useState<string>('');

  // use this syntax to trigger function on init
  React.useEffect(() => {
    loadWatchListFromStorage();
  }, []);

  const updateAppUrls = (token: string) => {
    setBeautyAdminUrl(`${ENV.BEAUTY_ADMIN_URL}/auth/login?tkn=${encodeURIComponent(token)}`);
    setBeautyCheckinUrl(`${ENV.BEAUTY_CHECKIN_URL}/auth?tkn=${encodeURIComponent(token)}`);
    setBeautyCheckoutUrl(`${ENV.BEAUTY_CHECKOUT_URL}/auth?tkn=${encodeURIComponent(token)}`);
  };

  const updateWatchList = (newWatchlist: TokenInfo[]) => {
    newWatchlist.sort((a, b) => {
      if (a.user.id < b.user.id) {
        return 1;
      }
      if (a.user.id > b.user.id) {
        return -1;
      }
      return 0;
    });
    setWatchList(newWatchlist);
    storageService.setItem(WATCHLIST_STORAGE_KEY, newWatchlist);
  };

  const addOrUpdateToken = async (token: string) => {
    const tokenInfo = await generateTokenInfo(token);

    if (!tokenInfo?.user?.email) {
      return false;
    }

    const matchedTokenInfo = watchList?.find(tk => tk.user?.email === tokenInfo.user.email);

    if (matchedTokenInfo) {
      // update token info
      const newWatchList = watchList.map(tk => (tk.user?.email === tokenInfo.user.email ? tokenInfo : tk));
      updateWatchList(newWatchList);
    } else {
      // add new token info
      const newWatchList = [...(watchList || []), tokenInfo];
      updateWatchList(newWatchList);
    }

    setSelectedTokenInfo(tokenInfo);
    updateAppUrls(tokenInfo?.token);

    return true;
  };

  const loginAndAddOrUpdateTokenInfo = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const token = await authenticationService.loginAndReturnTokenOnly(email, password);

      const isSuccess = await addOrUpdateToken(token);

      if (isSuccess) {
        hideLoginDialog();
      }
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const generateTokenInfo = async (token: string) => {
    try {
      setIsLoading(true);

      const tokenInfo: TokenInfo = {
        token,
        user: null,
        boList: [],
      };

      await Promise.all([
        userService.getCurrentLoggedInUserByToken(token).then(user => (tokenInfo.user = user)),
        businessObjectService
          .getAllBusinessObjectsOfUserByToken(token)
          .then(boList => (tokenInfo.boList = boList || [])),
      ]);

      setIsLoading(false);

      return tokenInfo;
    } catch (e) {
      logger.error(e);
      setIsLoading(false);

      return null;
    }
  };

  const loadWatchListFromStorage = () => {
    try {
      const data = storageService.getItem(WATCHLIST_STORAGE_KEY);
      if (data?.length > 0) {
        setWatchList(data);
      }
    } catch (e) {
      logger.error(e);
    }
  };

  const selectTokenHandler = (event: any) => {
    const token = event.target?.value;
    const matchedTokenInfo = watchList?.find(tk => tk.token === token);
    setSelectedTokenInfo(matchedTokenInfo);
    updateAppUrls(matchedTokenInfo?.token);
  };

  const showLoginDialog = () => {
    if (isLoading) {
      return;
    }
    setIsOpenDialog(true);
  };

  const hideLoginDialog = () => {
    if (isLoading) {
      return;
    }
    setIsOpenDialog(false);
  };

  return (
    <>
      <CreateNewUserTokenDialogComponent
        isOpen={isOpenDialog}
        isLoading={isLoading}
        onClose={hideLoginDialog}
        onSubmit={loginAndAddOrUpdateTokenInfo}
      ></CreateNewUserTokenDialogComponent>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <Button
            variant="contained"
            color="primary"
            disabled={isLoading}
            startIcon={<PersonAdd />}
            onClick={showLoginDialog}
          >
            Add new user to watch list
          </Button>
        </Grid>
        <Grid item xs={12} md={6} lg={8}>
          <TextField
            variant="outlined"
            margin="dense"
            select
            fullWidth
            label="Selected user"
            name="selectedUser"
            disabled={!watchList?.length}
            value={selectedTokenInfo?.token || ''}
            onChange={selectTokenHandler}
          >
            {watchList.map(tokenInfo => (
              <MenuItem value={tokenInfo.token} key={tokenInfo.token}>
                <div>
                  [{tokenInfo.user?.id}] [{tokenInfo.user?.email}] [{tokenInfo.user?.fullName}]
                </div>
                <ul>
                  {tokenInfo.boList?.map(bo => (
                    <li key={bo.id}>
                      [{bo.id}] [{bo.name}] [{bo.address}]
                    </li>
                  ))}
                </ul>
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {isLoading ? <LinearProgress /> : <></>}

      {selectedTokenInfo?.token ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: '30px' }}
            target="_blank"
            href={beautyAdminUrl}
          >
            Open Beauty Admin
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: '30px' }}
            target="_blank"
            href={beautyCheckoutUrl}
          >
            Open Beauty Checkout
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: '30px' }}
            target="_blank"
            href={beautyCheckinUrl}
          >
            Open Beauty Checkin
          </Button>
        </div>
      ) : (
        <></>
      )}
    </>
  );
}
